/**
 * **デュエルの舞台を 1 つ組む**（Issue #643 / #766。演出の確認 `/duel-editor`・タイミング調節 `/duel-timing` の共通）。
 *
 * ```
 * 素材（match）─┬→ 台本（editorPorts/duelScript.js: イベント列 → 帯・盤面・HUD・舞台の設定）
 *               │        │ frameAt(時計の ms)            ← 時計（clock）
 *               └→ 絵（pages/useDuelPicture.js: その時刻の台本 ＋ 素材 → 舞台の層）
 *                  SE（audio。台本の cues を鳴らす）
 * ```
 *
 * **台本と絵は「台本 ＋ 時計 → 絵」の口で分けてある**（docs/game-server.md「ゲームサーバーの外との約束」）。絵の側は `frame` しか受け取らない。
 * 位置合わせの 2 画面のプレビューもこの口の向こうにあるので、描く層を別のエンジン（ゲームサーバーの映像）に替えても台本の側は変わらない。
 *
 * **外から渡せるもの**（Issue #766。渡さなければ今までどおり内側で作る）:
 * - `match` … `useDuelMatch` の戻り値と同じ形（`loaded` / `head` / `players` / `cardAccess` / `fieldGenConfig` / `gameInfo` / `movieUrl`）。
 *   渡すと本番の API を読まない（描く側の演出ページはルームから受けた一式・増えていくイベント列で作る）
 * - `clock` … `ClockPort`（`duelEditor/timeline/ports/clock.js`）。渡すと元動画 / rAF / 手で進める時計を使わない
 *   （ライブは受けたフレームの pts、書き出しは手で進める）
 * - `zones` … 盤の位置（`overlay_zones` の形 `{ video_width, video_height, zones: { key: { x, y, w, h } } }`）。
 *   渡すと field_gen の config を見ない（収録中は補正サーバーの `/live/info.json` の `overlay_zones`）。`undefined` なら config から
 * - `audio: false` … SE を鳴らさない（収録中のゲーム映像。cues は今どおり作る）
 *
 * `settings` は完全な形（`editorPorts/timingSettings.js::SETTING_DEFAULTS` と同じ項目）。`shot` だけは名前でも数値のショットでもよい。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDuelMatch } from './useDuelMatch';
import { useDuelPicture } from './useDuelPicture';
import { createDuelScript } from '../editorPorts/duelScript';
import { connectClock } from '../duelEditor/timeline/ports/clock';
import { useEditorState } from '../duelTimeline/useEditorState';
import { createRafClock } from '../duelTimeline/rafClock';
import { createVideoClock } from '../editorPorts/videoClock';
import { createEditAssetsPort } from '../editorPorts/editAssets';
import { createWebAudioPort } from '../editorPorts/webAudio';

export { shotOf } from './useDuelPicture';

/**
 * @param gameUuid 試合（空なら読まない。`match` を渡したら見ない）
 * @param replayId 開く replay（null なら一番新しいもの）
 * @param sample サンプルの試合（`{ loaded, head }`。動画なし・rAF の時計）か null
 * @param theme 今のテーマ
 * @param settings 舞台の設定（完全な形。`shot` は名前か数値）
 * @param stage テーマの既定に重ねる舞台の設定（画質・テーマの見た目の値など）
 * @param extra 層に渡す追加の設定（テーマを詰める道具の `editing` …）。**フレームの関数**でもよい
 *   （`(frame) => ({ objectTweaks })`。フレームから組むものを 1 フレーム遅らせないため）
 * @param match 外から渡す素材（`useDuelMatch` の戻り値の形）。null なら `gameUuid` から読む
 * @param clock 外から渡す時計（`ClockPort`）。null なら内側で選ぶ
 * @param zones 外から渡す盤の位置（`overlay_zones` の形）。`undefined` なら field_gen の config から
 * @param audio false なら SE を鳴らさない
 */
export function useDuelStage({
  gameUuid, replayId = null, sample = null, theme, settings, stage = null, extra = null,
  match: matchIn = null, clock: clockIn = null, zones = undefined, audio: audioOn = true,
}) {
  // ---- 素材（外から渡されたら本番を読まない）
  const fetched = useDuelMatch(matchIn ? null : gameUuid, replayId);
  const match = matchIn ?? fetched;
  const { fieldGenConfig, loaded, head: realHead, players, movieUrl: lightMovieUrl, cardAccess } = match;
  const movieUrl = lightMovieUrl ?? null;

  // ---- 台本（演出の型は全テーマの和で 1 回だけ作り、中身は**そのとき**のテーマから引く。Issue #541）
  const themeRef = useRef(theme);
  themeRef.current = theme;
  const [script] = useState(() => createDuelScript({ getTheme: () => themeRef.current }));
  const { editor } = script;
  useEditorState(editor);
  const shownLoaded = sample ? sample.loaded : loaded;
  const head = sample ? sample.head : realHead;
  useEffect(() => {
    script.setMatch({
      state: shownLoaded?.state, replay: shownLoaded?.replay, head, players,
    });
  }, [script, shownLoaded, head, players, theme]);

  // ---- 時計: 外から渡されたらそれ。無ければ 実試合は元動画、サンプルは rAF
  const [videoClock] = useState(() => createVideoClock());
  const [rafClock] = useState(() => createRafClock());
  const activeClock = clockIn ?? (sample ? rafClock : videoClock);
  useEffect(() => connectClock(editor, activeClock), [editor, activeClock]);
  useEffect(() => () => { videoClock.dispose(); rafClock.dispose?.(); }, [videoClock, rafClock]);
  /** 移動（時計を動かせば editor も付いてくる） */
  const seekTo = useCallback((ms) => { activeClock.seek(ms); editor.seek(ms); }, [activeClock, editor]);
  /** 再生 / 一時停止（実試合は元動画、サンプルは rAF の時計） */
  const togglePlay = useCallback(() => {
    if (activeClock.paused()) activeClock.play(1); else activeClock.pause();
  }, [activeClock]);

  // ---- 元動画（素材）
  const [videoEl, setVideoEl] = useState(null);
  const [videoSize, setVideoSize] = useState(null);
  // 要素が元の映像のどの矩形か（描く側は平面に入る部分だけ渡す。Issue #808）。null なら全体
  const [videoCrop, setVideoCrop] = useState(null);
  const attachVideo = useCallback((node) => { setVideoEl(node); if (node) videoClock.attach(node); }, [videoClock]);
  useEffect(() => { setVideoSize(null); setVideoCrop(null); }, [gameUuid]);

  // ---- SE（何をいつ鳴らすかは台本の cues。鳴らすのは webAudio。`audio: false` なら鳴らさない）
  const [assetsPort] = useState(() => createEditAssetsPort());
  const [audioPort] = useState(() => createWebAudioPort({
    urlOf: (name) => assetsPort.url(`se/${name}.mp3`),
    clock: { ms: () => editor.transport.ms },
  }));
  const [seOn, setSeOn] = useState(true);
  useEffect(() => { if (audioOn) assetsPort.refresh?.(); return () => audioPort.dispose?.(); }, [assetsPort, audioPort, audioOn]);
  const playing = Boolean(editor.transport.playing);
  const cues = editor.timeline?.cues;
  useEffect(() => {
    if (audioOn && playing && seOn) audioPort.schedule(cues ?? [], { volume: 0.8 });
    else audioPort.stop();
    return () => audioPort.stop();
  }, [audioOn, playing, seOn, cues, audioPort]);

  // ---- 絵（その時刻の台本 ＋ 素材 → 舞台の層）
  // **同じ zones なら同じオブジェクト**: 毎回包み直すと盤（Field）→ view → 描く設定が毎フレーム作り直され、
  // 盤の紙が毎フレーム描き直しになる（2026-10-10 のプロファイルで JS の約 3 割）
  const zoneConfig = useMemo(
    () => (zones !== undefined ? { overlay_zones: zones } : fieldGenConfig),
    [zones, fieldGenConfig],
  );
  const frame = script.frameAt(editor.transport.ms);
  const picture = useDuelPicture({
    frame, theme, settings, stage, extra,
    players, cards: cardAccess, board: shownLoaded?.state?.board ?? null,
    zoneConfig,
    video: { el: videoEl, size: videoSize, crop: videoCrop },
  });

  return {
    match, movieUrl, script, editor, activeClock, seekTo, togglePlay, playing,
    videoEl, videoSize, setVideoSize, setVideoCrop, attachVideo,
    assetsPort, seOn: audioOn && seOn, setSeOn, frame, ...picture,
  };
}
